// F-112 : coûts unitaires (hypothèses), marges par offre ; F-117 : attribution et revenus d'apport hypothétiques.
import { useState } from "react";
import { post, put } from "@core/api";
import { date, money, percent } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Empty, ErrorBox, Input, Loading, Modal, Notice, PageHeader, Select, Table, Tabs, fieldError, useToast } from "@core/ui";
import { LEAD_STATUS, PLAN_CADENCE, PRODUCT_LABEL, microEuros, parseMicroEuros, useAction } from "./shared";

type Margin = {
  revenue_ttc_cents: number;
  revenue_ht_cents: number;
  vat_cents: number;
  payment_fees_cents: number;
  variable_costs_cents: number;
  lines: { code: string; label: string; quantity_milli: number; unit_cost_microcents: number; amount_cents: number }[];
  margin_cents: number;
  margin_rate_bp: number | null;
};

type Tab = "marges" | "couts" | "revenus";

export function CostsPage() {
  const [tab, setTab] = useState<Tab>("marges");
  return (
    <div className="page">
      <PageHeader eyebrow="Espace" title="Coûts et validation commerciale" intro="Outil interne : coûts unitaires, marges estimées par offre et revenus d’apport. Tout y est hypothèse tant que les devis et contrats ne sont pas obtenus." />
      <Notice tone="attention" title="Hypothèses non validées">
        Prix, coûts et usages types servent à préparer les arbitrages D-03 et D-28. Une marge estimée n’est ni un chiffre d’affaires, ni un encaissement.
      </Notice>
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "marges", label: "Marges par offre" },
          { value: "couts", label: "Coûts unitaires" },
          { value: "revenus", label: "Revenus partenaires" },
        ]}
      />
      {tab === "marges" ? <MarginsTab /> : null}
      {tab === "couts" ? <CostsTab /> : null}
      {tab === "revenus" ? <RevenuesTab /> : null}
    </div>
  );
}

function MarginLines({ m }: { m: Margin }) {
  return (
    <Table caption="Détail de la marge">
      <tbody>
        <tr>
          <td>Prix TTC</td>
          <td className="right num">{money(m.revenue_ttc_cents)}</td>
        </tr>
        <tr>
          <td>Revenu hors taxes</td>
          <td className="right num">{money(m.revenue_ht_cents)}</td>
        </tr>
        <tr>
          <td>Frais de paiement</td>
          <td className="right num">− {money(m.payment_fees_cents)}</td>
        </tr>
        {m.lines.map((l) => (
          <tr key={l.code}>
            <td className="small">
              {l.label} <span className="muted">× {(l.quantity_milli / 1000).toLocaleString("fr-FR", { maximumFractionDigits: 3 })} à {microEuros(l.unit_cost_microcents)}</span>
            </td>
            <td className="right num">− {money(l.amount_cents)}</td>
          </tr>
        ))}
        <tr>
          <td>
            <strong>Marge estimée</strong>
          </td>
          <td className="right num">
            <strong>{money(m.margin_cents)}</strong>
            {m.margin_rate_bp !== null ? <span className="block small muted">{percent(m.margin_rate_bp)} du revenu HT</span> : null}
          </td>
        </tr>
      </tbody>
    </Table>
  );
}

function MarginsTab() {
  const org = useOrg();
  const q = useGet<any>(org.api("/costs-margins"));
  const [scenario, setScenario] = useState<"typical" | "observed">("typical");
  const [sim, setSim] = useState<{ plan: string; quantities: Record<string, string> } | null>(null);
  const [simResult, setSimResult] = useState<Margin | null>(null);
  const action = useAction();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;
  const openSim = (p: any) => {
    const quantities: Record<string, string> = {};
    for (const [code, milli] of p.typical.usage as [string, number][]) quantities[code] = String(milli / 1000).replace(".", ",");
    setSimResult(null);
    setSim({ plan: p.code, quantities });
  };
  const runSim = async () => {
    if (!sim) return;
    const quantities: Record<string, number> = {};
    for (const [k, v] of Object.entries(sim.quantities)) quantities[k] = Number(v.replace(",", ".")) || 0;
    const r = await action.run(() => post<any>(org.api("/costs-simulation"), { plan_code: sim.plan, quantities }));
    if (r) setSimResult(r.margin);
  };
  return (
    <div className="stack">
      <Tabs
        value={scenario}
        onChange={setScenario}
        items={[
          { value: "typical", label: "Usage type (hypothèse)" },
          { value: "observed", label: "Usage constaté de cet espace (30 jours)" },
        ]}
      />
      <div className="grid-2">
        {d.plans.map((p: any) => (
          <Card
            key={p.code}
            title={p.name}
            actions={
              <>
                {p.price_hypothesis ? <Badge tone="attention">Prix à valider</Badge> : null}
                <Button variant="discret" small onClick={() => openSim(p)}>
                  Simuler
                </Button>
              </>
            }
          >
            <p className="small muted">
              {p.cadence === "gratuit" ? "Gratuit" : `${money(p.price_cents)} ${p.price_is_ttc ? "TTC" : "HT"} ${PLAN_CADENCE[p.cadence] ?? ""}`} · période de calcul : {p.cadence === "campagne" ? "une campagne" : "un mois"}
            </p>
            <MarginLines m={p[scenario].margin} />
          </Card>
        ))}
      </div>
      {scenario === "observed" && d.observed_unvalued.length ? (
        <Notice>Appels non valorisés faute de coût unitaire : {d.observed_unvalued.map((x: any) => `${x.adapter} · ${x.operation} (${x.appels})`).join(", ")}.</Notice>
      ) : null}
      <p className="small muted">{d.note}</p>
      <Modal
        open={!!sim}
        onClose={() => setSim(null)}
        title="Simuler un usage"
        footer={
          <>
            <Button variant="discret" onClick={() => setSim(null)}>
              Fermer
            </Button>
            <Button busy={action.busy} onClick={runSim}>
              Calculer
            </Button>
          </>
        }
      >
        {sim ? (
          <div className="stack-sm">
            <ErrorBox error={action.error} />
            <Select label="Offre" value={sim.plan} options={d.plans.map((p: any) => ({ value: p.code, label: p.name }))} onChange={(e) => setSim({ ...sim, plan: e.target.value })} />
            <div className="form-row">
              {Object.entries(sim.quantities).map(([code, v]) => (
                <Input key={code} label={code.replace(/_/g, " ")} inputMode="decimal" value={v} onChange={(e) => setSim({ ...sim, quantities: { ...sim.quantities, [code]: e.target.value } })} />
              ))}
            </div>
            {simResult ? <MarginLines m={simResult} /> : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function CostsTab() {
  const org = useOrg();
  const q = useGet<any>(org.api("/costs"));
  const invalidate = useInvalidate();
  const toast = useToast();
  const action = useAction();
  const [edit, setEdit] = useState<{ code: string; label: string; euros: string; percent: string; source: string } | null>(null);
  const [local, setLocal] = useState<string | undefined>();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const save = async () => {
    if (!edit) return;
    const micro = parseMicroEuros(edit.euros);
    const pct = edit.percent.trim() === "" ? 0 : Math.round(Number(edit.percent.replace(",", ".")) * 100);
    if (micro === null || Number.isNaN(pct)) {
      setLocal("Indiquez un montant en euros, par exemple 0,02 ou 1,50.");
      return;
    }
    setLocal(undefined);
    const r = await action.run(() => put(org.api(`/costs/${edit.code}`), { unit_cost_microcents: micro, percent_bp: pct, source: edit.source }));
    if (r) {
      toast("Hypothèse enregistrée.");
      setEdit(null);
      invalidate(org.api("/costs"));
    }
  };
  return (
    <Card title="Coûts unitaires">
      <p className="small muted">{q.data.note}</p>
      <Table caption="Coûts unitaires">
        <thead>
          <tr>
            <th>Opération</th>
            <th className="right">Coût</th>
            <th>Source</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {q.data.items.map((c: any) => (
            <tr key={c.id}>
              <td>
                {c.label}
                <span className="block small muted">{c.unit}</span>
              </td>
              <td className="right num">
                {microEuros(c.unit_cost_microcents)}
                {c.percent_bp ? ` + ${percent(c.percent_bp, 2)}` : ""}
              </td>
              <td className="small">
                <Badge tone="attention">Hypothèse</Badge> {c.source}
                <span className="block muted">Mis à jour le {date(c.updated_at)}</span>
              </td>
              <td className="right">
                <Button variant="discret" small onClick={() => setEdit({ code: c.code, label: c.label, euros: String(c.unit_cost_microcents / 1e8).replace(".", ","), percent: c.percent_bp ? String(c.percent_bp / 100).replace(".", ",") : "", source: c.source })}>
                  Modifier
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Modal
        open={!!edit}
        onClose={() => setEdit(null)}
        title={edit ? `Coût : ${edit.label}` : ""}
        footer={
          <>
            <Button variant="discret" onClick={() => setEdit(null)}>
              Annuler
            </Button>
            <Button busy={action.busy} onClick={save}>
              Enregistrer
            </Button>
          </>
        }
      >
        {edit ? (
          <div className="stack-sm">
            <ErrorBox error={action.error && !fieldError(action.error, "source") ? action.error : null} />
            <div className="plateforme-champ-euros">
              <Input label="Coût unitaire en euros" inputMode="decimal" value={edit.euros} error={local ?? fieldError(action.error, "unit_cost_microcents")} help="Jusqu’à 6 décimales, par exemple 0,002." onChange={(e) => setEdit({ ...edit, euros: e.target.value })} />
            </div>
            {edit.code === "paiement" ? <Input label="Pourcentage du montant (%)" inputMode="decimal" value={edit.percent} onChange={(e) => setEdit({ ...edit, percent: e.target.value })} /> : null}
            <Input label="Source de l’hypothèse" required value={edit.source} error={fieldError(action.error, "source")} help="Devis, grille publique datée ou estimation." onChange={(e) => setEdit({ ...edit, source: e.target.value })} />
          </div>
        ) : null}
      </Modal>
    </Card>
  );
}

const REVENUE_STATUS: Record<string, string> = { estimee: "Estimée", acquise: "Acquise", facturee: "Facturée", encaissee: "Encaissée", annulee: "Annulée" };
const SOURCE_LABEL: Record<string, string> = { campagne: "Campagne", logement: "Fiche logement", espace: "Espace" };

function RevenuesTab() {
  const org = useOrg();
  const q = useGet<any>(org.api("/insurance/attribution"));
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  return (
    <div className="stack">
      <Notice tone="attention">{q.data.note}</Notice>
      <div className="grid-2">
        <Card title="Demandes par origine et statut">
          {q.data.funnel.length === 0 ? (
            <Empty title="Aucune demande." />
          ) : (
            <Table caption="Conversion">
              <thead>
                <tr>
                  <th>Partenaire</th>
                  <th>Origine</th>
                  <th>Statut</th>
                  <th className="right">Nombre</th>
                </tr>
              </thead>
              <tbody>
                {q.data.funnel.map((f: any, i: number) => (
                  <tr key={i}>
                    <td>
                      {f.partner}
                      <span className="block small muted">{PRODUCT_LABEL[f.product] ?? f.product}</span>
                    </td>
                    <td>{SOURCE_LABEL[f.source] ?? f.source}</td>
                    <td>{LEAD_STATUS[f.status]?.label ?? f.status}</td>
                    <td className="right num">{f.count}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
        <Card title="Revenus d’apport (hypothèses)">
          {q.data.revenues.length === 0 ? (
            <Empty title="Aucun revenu estimé." />
          ) : (
            <Table caption="Revenus">
              <thead>
                <tr>
                  <th>Partenaire</th>
                  <th>Statut</th>
                  <th className="right">Montant</th>
                </tr>
              </thead>
              <tbody>
                {q.data.revenues.map((r: any, i: number) => (
                  <tr key={i}>
                    <td>
                      {r.partner}
                      <span className="block small muted">{PRODUCT_LABEL[r.product] ?? r.product}</span>
                    </td>
                    <td>
                      {REVENUE_STATUS[r.status] ?? r.status} ({r.count}) <Badge tone="attention">Hypothèse</Badge>
                    </td>
                    <td className="right num">{money(r.amount_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          <p className="small muted">Séparés des abonnements et des loyers. Aucun montant n’est acquis sans contrat.</p>
        </Card>
      </div>
    </div>
  );
}
